import {
  evaluateTemplateValue,
  dataLocationToPath,
  joinData as coreJoinData,
} from '@ea-lab/reactive-json';

/**
 * Réaction générique SYNCHRONE « concaténer » : écrit la chaîne obtenue en
 * joignant `parts` (verbatim, sans encodage) dans le chemin `target` du store,
 * dans l'ordre de la chaîne d'actions.
 *
 * Pourquoi une réaction (échappatoire config-first) : reactive-json ne compose
 * AUCUNE chaîne en YAML — ni `setData`, ni `setAttributeValue`, ni
 * `simpleMapping` (même constat que [navigateToReaction.js](./navigateToReaction.js),
 * qui règle le cas particulier de la navigation). Or `submitData` / `fetchData`
 * évaluent leur `url` comme UNE valeur : une URL qui dépend d'une donnée choisie
 * à l'exécution (l'entité visée par `/decoupled-api/save?…&id=<nid>`) est donc
 * inatteignable sans concaténation. La capacité manquante est la concaténation
 * elle-même : cette réaction ne fait que ça, et sert partout (URL, libellé…).
 *
 * `parts` : tableau (ou valeur unique) de morceaux, chacun évalué comme une
 * valeur reactive-json (`~~.foo` → donnée du store ; toute autre chaîne reste
 * littérale ; les placeholders d'événement sont déjà résolus par le moteur).
 *
 * Garde-fou : un morceau vide / absent (`null`, `undefined`, `''`) annule
 * l'écriture — mieux vaut ne rien écrire qu'une URL amputée (`…&id=`), qui
 * viserait une autre entité (ou une création).
 *
 * Usage YAML (lier une entreprise fille = écrire SA société mère) :
 *   - { what: joinData, on: click, target: ~~.linkChildUrl,
 *       parts: [ "/decoupled-api/save?display=decoupled&entity_type=node&bundle=company&id=", ~~.linkChildId ] }
 *   - { what: submitData, on: click, url: ~~.linkChildUrl, httpMethod: POST,
 *       data: { data: ~~.linkChildPayload }, updateOnlyData: true,
 *       updateDataAtLocation: ~~.linkChildAck }
 *
 * Enregistrée dans [App.jsx](../../../App.jsx) sous `customPlugins.reaction.joinData`.
 */
const legacyJoinData = (props) => {
  const { args, globalDataContext, templateContext } = props;
  if (!args || args.parts === undefined || args.parts === null) return;

  const gdc = globalDataContext?.getRootContext
    ? globalDataContext.getRootContext()
    : globalDataContext;

  const path = typeof args.target === 'string'
    ? dataLocationToPath({
      dataLocation: args.target,
      currentPath: templateContext?.templatePath,
      globalDataContext: gdc,
      templateContext,
    })
    : null;
  if (!path) return;

  const parts = Array.isArray(args.parts) ? args.parts : [args.parts];
  const resolved = [];
  for (const part of parts) {
    const value = evaluateTemplateValue({
      valueToEvaluate: part,
      globalDataContext: gdc,
      templateContext,
    });
    if (value === null || value === undefined || value === '') {
      console.warn('joinData: morceau vide, écriture annulée.', args.parts);
      return;
    }
    resolved.push(String(value));
  }

  gdc.updateData(resolved.join(''), path);
};

/**
 * Le cœur reactive-json a son propre `joinData` depuis 2.11, sous le MÊME nom :
 * `{path, parts}`, où `parts` est une liste de segments (`{segment, required}`)
 * et un morceau vide vaut `""` — sauf `required: true`, qui annule.
 *
 * Du contenu existant (YAML versionnés ET défs de blocs en base) écrit encore
 * l'ancienne forme `{target, parts}`, qui annule sur TOUT morceau vide — et
 * certains usages comptent dessus (URL de mise à jour qui deviendrait une URL
 * de création, `displayName` qui ne doit changer que si prénom ET nom sont
 * remplis). On aiguille donc sur la forme des paramètres : `target` → ancienne
 * réaction, sinon celle du cœur. Pour du contenu neuf : la forme du cœur, avec
 * `required: true` sur chaque identifiant.
 */
export const joinDataReaction = (props) =>
  (props?.args?.target !== undefined ? legacyJoinData(props) : coreJoinData(props));
